import { useState, useEffect } from 'preact/hooks';

type DisplayMode = 'map' | 'list';

const MOBILE_BREAKPOINT = 768;

/**
 * Hook to manage map/list display mode.
 * On mobile (< 768px), forces list view and hides the toggle.
 * On desktop, allows toggling between map and list.
 *
 * @return {Object} Display mode state and helpers.
 */
export function useDisplayMode() {
	const isMobileQuery = () => ! window.matchMedia( `(min-width: ${ MOBILE_BREAKPOINT }px)` ).matches;

	const [ isMobile, setIsMobile ] = useState( isMobileQuery );
	const [ displayMode, setDisplayMode ] = useState< DisplayMode >( () => isMobileQuery() ? 'list' : 'map' );

	// Listen for viewport changes across the mobile breakpoint.
	useEffect( () => {
		const mql = window.matchMedia( `(min-width: ${ MOBILE_BREAKPOINT }px)` );

		const handleChange = ( e: MediaQueryListEvent ) => {
			const nowMobile = ! e.matches;
			setIsMobile( nowMobile );

			// Force list view on mobile, restore map view on desktop.
			setDisplayMode( nowMobile ? 'list' : 'map' );
		};

		mql.addEventListener( 'change', handleChange );

		return () => {
			mql.removeEventListener( 'change', handleChange );
		};
	}, [] );

	return { displayMode, setDisplayMode, isMobile };
}
